import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Bell, Download, LogIn, Share, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { api, errMsg } from "@/lib/api";
import { isIOS, isStandalone, setManifest } from "@/lib/push";
import { useAuth } from "@/context/AuthContext";
import { brandClass, brandStyle } from "@/lib/brandTheme";
import { usePageMeta } from "@/lib/seo";

const Step = ({ n, icon: Icon, title, children, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testId}>
    <p className="flex items-center gap-2 font-display text-lg font-semibold"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-600 text-sm text-white">{n}</span><Icon className="h-5 w-5 text-indigo-600" />{title}</p>
    <div className="mt-2 text-sm text-slate-600">{children}</div>
  </div>
);

const InstallStep = () => {
  const [prompt, setPrompt] = useState(() => window.__installPrompt || null);
  useEffect(() => {
    const h = () => setPrompt(window.__installPrompt);
    window.addEventListener("installprompt-ready", h);
    return () => window.removeEventListener("installprompt-ready", h);
  }, []);
  if (isStandalone()) return <p data-testid="join-installed">The app is installed. You're using it now.</p>;
  if (isIOS()) return <p data-testid="join-ios-steps">In Safari, tap <Share className="inline h-4 w-4" /> <b>Share</b>, then <b>Add to Home Screen</b>, then <b>Add</b>. Open the new icon on your Home Screen.</p>;
  if (prompt) return (
    <Button className="mt-1 rounded-full" data-testid="join-install-btn" onClick={async () => {
      prompt.prompt();
      await prompt.userChoice;
      window.__installPrompt = null;
      setPrompt(null);
    }}><Download className="mr-1 h-4 w-4" />Install the app</Button>
  );
  return <p data-testid="join-browser-steps">Open your browser menu (⋮) and choose <b>Install app</b> or <b>Add to Home screen</b>. On a computer, look for the install icon in the address bar.</p>;
};

export default function Join() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [biz, setBiz] = useState(null);
  const [error, setError] = useState("");
  usePageMeta("join", biz ? { business: biz.name } : null);
  useEffect(() => {
    setManifest(`${process.env.REACT_APP_BACKEND_URL}/api/public/manifest/${slug}`);
    api.get(`/public/join/${slug}`).then((r) => {
      if (r.data.slug !== slug) {
        nav(`/join/${r.data.slug}${window.location.search}`, { replace: true });
        return;
      }
      setBiz(r.data);
      if (r.data.colors?.primary) {
        document.querySelector("meta[name=theme-color]")?.setAttribute("content", r.data.colors.primary);
      }
      if (r.data.brand) {
        document.querySelector("link[rel=apple-touch-icon]")?.setAttribute("href", `${process.env.REACT_APP_BACKEND_URL}${r.data.brand.icon_180}`);
      }
    }).catch((e) => setError(errMsg(e)));
    return () => {
      setManifest("/manifest.json");
      document.querySelector("meta[name=theme-color]")?.setAttribute("content", "#4f46e5");
      document.querySelector("link[rel=apple-touch-icon]")?.setAttribute("href", "/icons/icon-180.png");
    };
  }, [slug, nav]);
  useEffect(() => {
    if (params.get("app") && user) {
      nav(user.business_id && user.tenant_role === "admin" ? "/app" : "/me", { replace: true });
    }
  }, [params, user, nav]);

  if (error) return <div className="mx-auto max-w-md p-8 text-center" data-testid="join-error"><Logo /><p className="mt-6 text-slate-600">{error}</p></div>;
  if (!biz) return null;
  return (
    <div className={`min-h-screen bg-slate-50 ${brandClass(biz.colors)}`} style={brandStyle(biz.colors)}>
      <div className="mx-auto max-w-lg space-y-4 px-5 py-10">
        {biz.brand ? (
          <div className="flex items-center justify-between">
            <img src={`${process.env.REACT_APP_BACKEND_URL}${biz.brand.logo}`} alt={`${biz.name} logo`} className="max-h-16 max-w-[200px] object-contain" data-testid="join-brand-logo" />
            <span className="text-xs text-slate-400">Powered by StaffRotas</span>
          </div>
        ) : <Logo />}
        <div className="pt-4">
          <p className="text-sm font-semibold uppercase tracking-wider text-indigo-600">Staff app</p>
          <h1 className="font-display text-4xl font-bold tracking-tight" data-testid="join-business-name">{biz.name}</h1>
          <p className="mt-2 text-slate-600">See your shifts, swap and pick up extra shifts, and get notified the moment the rota changes.</p>
        </div>
        <Step n={1} icon={Smartphone} title="Add the app to your phone" testId="join-step-install"><InstallStep /></Step>
        <Step n={2} icon={LogIn} title="Log in" testId="join-step-login">
          {user ? <Button asChild className="mt-1 rounded-full" data-testid="join-open-btn"><Link to="/me">Open my shifts</Link></Button> : (
            <>
              <p>Use the email and password your manager set up for you.</p>
              <Button asChild className="mt-3 rounded-full" data-testid="join-login-btn"><Link to="/login">Log in</Link></Button>
            </>
          )}
        </Step>
        <Step n={3} icon={Bell} title="Turn on notifications" testId="join-step-notify">
          <p>On your Home page, tap <b>Turn on notifications</b> to get rota updates and extra shift offers.</p>
        </Step>
      </div>
    </div>
  );
}
